import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { ApiClient } from "../../api/client";
import type { CustomerDetail, PlatformCaller } from "../../api/types";
import { t } from "../../i18n";
import { phoneDigits, phoneOnLeave } from "../../lib/phone";
import { GrowingTextarea } from "../../ui/GrowingTextarea";
import type { Draft } from "../app-storage";

/** What the waiter types for a takeaway; the lines are elsewhere. */
export type TakeawayDetails = Pick<Draft, "name" | "phone" | "email" | "note">;

/**
 * The customer and the note of a takeaway as an order sends them: only what is filled in. A phone number makes
 * the server remember the customer (name, e-mail), as at the till.
 */
export function takeawayRequest(draft: TakeawayDetails): { customer?: Record<string, string>; note?: string } {
  const customer = Object.fromEntries(
    (
      [
        ["phone", draft.phone ?? ""],
        ["name", draft.name],
        ["email", draft.email ?? ""],
      ] as const
    )
      .map(([field, value]) => [field, value.trim()] as const)
      .filter(([, value]) => value !== ""),
  );
  const note = (draft.note ?? "").trim();
  return {
    ...(Object.keys(customer).length > 0 && { customer }),
    ...(note !== "" && { note }),
  };
}

/**
 * "Afhaalgegevens" in the waiter's app, as on the till (Furkan, 19/10/2026: "tıpkı kasadaki gibi"): the phone
 * number, a known one fills in the name and the e-mail and shows who it is; the name; the e-mail for the
 * confirmation; and the note on the receipt. The customer's internal note is shown, never sent.
 */
export function TakeawayCustomer({
  api,
  details,
  onChange,
}: {
  api: ApiClient;
  details: TakeawayDetails;
  onChange: (change: Partial<TakeawayDetails>) => void;
}) {
  const text = t.customer;
  const digits = phoneDigits(details.phone ?? "");
  const [found, setFound] = useState<{
    digits: string;
    customer: CustomerDetail | null;
    platform: PlatformCaller | null;
  } | null>(null);
  /** The number whose customer was filled in: typing on never overwrites the fields again. */
  const filled = useRef<string | null>(null);
  /** A known number: its name and e-mail into the fields that are still empty, once per number. */
  const fill = useEffectEvent((number: string, customer: CustomerDetail | null, platform: PlatformCaller | null) => {
    setFound({ digits: number, customer, platform });
    if (filled.current === number) return;
    filled.current = number;
    const name = customer !== null && customer.name !== "" ? customer.name : (platform?.name ?? "");
    const email = customer?.email ?? "";
    onChange({
      ...(details.name.trim() === "" && name !== "" && { name }),
      ...((details.email ?? "").trim() === "" && email !== "" && { email }),
    });
  });

  useEffect(() => {
    if (digits.length < 9) return;
    let cancelled = false;
    api
      .request<{ customer: CustomerDetail | null; platform?: PlatformCaller | null }>(
        "GET",
        `/device/customers/lookup?phone=${digits}`,
      )
      .then((answer) => {
        if (!cancelled) fill(digits, answer.customer, answer.platform ?? null);
      })
      .catch(() => {
        // offline or refused: the waiter types the name himself
      });
    return () => {
      cancelled = true;
    };
  }, [api, digits]);

  const shown = found?.digits === digits ? found : null;
  const known = shown?.customer ?? null;
  return (
    <section className="ob-cust" aria-label={t.tafoApp.waiter.takeawayDetails}>
      <div className="ph-sec">{t.tafoApp.waiter.takeawayDetails}</div>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="off"
        value={details.phone ?? ""}
        placeholder={text.phoneTouch}
        aria-label={text.phoneLabel}
        onChange={(event) => {
          onChange({ phone: event.target.value });
        }}
        onBlur={(event) => {
          const tidy = phoneOnLeave(event.target.value);
          if (tidy !== null) onChange({ phone: tidy });
        }}
      />
      {known !== null && (
        <p className="ob-known">
          <b>{known.name || text.card.noName}</b> · {known.orders > 0 ? text.card.regular : text.card.new}
          {known.orders > 0 && (
            <>
              {" "}
              · {known.orders} {text.card.orders}
            </>
          )}
          {known.note.trim() !== "" && (
            <span className="ob-knownnote">
              {text.intern}: {known.note}
            </span>
          )}
        </p>
      )}
      {known === null && shown?.platform && (
        <p className="ob-known">{text.fromPlatform(shown.platform.orders, shown.platform.platform)}</p>
      )}
      <input
        value={details.name}
        maxLength={100}
        autoComplete="off"
        placeholder={text.name}
        aria-label={text.name}
        onChange={(event) => {
          onChange({ name: event.target.value });
        }}
      />
      <input
        type="email"
        inputMode="email"
        autoComplete="off"
        value={details.email ?? ""}
        maxLength={200}
        placeholder={text.email}
        aria-label={text.emailLabel}
        onChange={(event) => {
          onChange({ email: event.target.value });
        }}
      />
      <GrowingTextarea
        value={details.note ?? ""}
        maxLength={500}
        placeholder={t.tafoApp.waiter.takeawayNote}
        aria-label={text.note}
        onChange={(note) => {
          onChange({ note });
        }}
      />
    </section>
  );
}
